import { useCallback, useEffect, useMemo, useState } from 'react';
import { format, parseISO, differenceInDays, startOfDay } from 'date-fns';
import { Copy, Download, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useApp } from '@/contexts/AppContext';
import AppDateField from '@/components/AppDateField';
import type { WorkInvoice, WorkInvoiceStatus } from '@/types';
import {
  buildWorkInvoiceChaseDraft,
  buildWorkInvoiceFileName,
  buildWorkInvoiceMailtoUrl,
  buildWorkInvoicePdfBytes,
  buildWorkInvoiceReminderQueue,
  getWorkInvoiceReminder,
} from '../../../ui-foundation/features/work';
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppCardBody,
  AppCardHeader,
  AppEmptyState,
  AppFieldGroup,
  AppInput,
  AppLoadingState,
  AppText,
  AppTextArea,
} from '../../../ui-foundation';

const STATUS_CLASSES: Record<WorkInvoiceStatus, string> = {
  draft: 'bg-muted text-muted-foreground',
  sent: 'bg-plum-light text-primary',
  chasing: 'bg-mustard-light text-accent',
  paid: 'bg-success-light text-success',
};

function displayStatus(invoice: WorkInvoice) {
  if (invoice.status !== 'paid' && differenceInDays(parseISO(invoice.dueDate), startOfDay(new Date())) < 0) {
    return 'Overdue';
  }

  if (invoice.status === 'chasing') return 'Chasing';
  if (invoice.status === 'sent') return 'Sent';
  if (invoice.status === 'paid') return 'Paid';
  return 'Draft';
}

function statusClass(invoice: WorkInvoice) {
  if (invoice.status !== 'paid' && differenceInDays(parseISO(invoice.dueDate), startOfDay(new Date())) < 0) {
    return 'bg-red-100 text-red-800';
  }

  return STATUS_CLASSES[invoice.status];
}

function notifySectionRefresh() {
  window.dispatchEvent(new CustomEvent('adulting:data-changed'));
}

async function downloadInvoicePdf(invoice: WorkInvoice) {
  const pdfBytes = await buildWorkInvoicePdfBytes(invoice);
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  const downloadUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = buildWorkInvoiceFileName(invoice);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(downloadUrl);
}

type InvoiceTrackerProps = {
  onDataChanged?: () => void | Promise<void>;
};

function buildPaidIncomeNotes(invoice: WorkInvoice, paidDate: string) {
  const base = `Auto-logged from paid invoice ${invoice.invoiceNumber} for ${invoice.clientName} on ${format(parseISO(paidDate), 'd MMM yyyy')}.`;
  return invoice.notes.trim() ? `${base}\n\nInvoice notes:\n${invoice.notes.trim()}` : base;
}

export default function InvoiceTracker({ onDataChanged }: InvoiceTrackerProps) {
  const { user } = useApp();
  const [invoices, setInvoices] = useState<WorkInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [payingInvoiceId, setPayingInvoiceId] = useState<string | null>(null);
  const [number, setNumber] = useState(`INV-${new Date().getFullYear()}-${String(Date.now()).slice(-4)}`);
  const [clientName, setClientName] = useState('');
  const [issueDate, setIssueDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState(new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');

  const openInvoiceEmailDraft = (invoice: WorkInvoice) => {
    window.location.href = buildWorkInvoiceMailtoUrl(invoice);
  };

  const handleInvoiceDownload = async (invoice: WorkInvoice) => {
    await downloadInvoicePdf(invoice);
    toast.success('Invoice PDF downloaded.');
  };

  const copySendDraft = async (invoice: WorkInvoice) => {
    openInvoiceEmailDraft(invoice);
    toast.success('Invoice email draft opened.');
  };

  const copyChaseDraft = async (invoice: WorkInvoice) => {
    await navigator.clipboard.writeText(buildWorkInvoiceChaseDraft(invoice));
    toast.success('Invoice chase draft copied.');
  };

  const fetchInvoices = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from('work_invoices' as any)
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    setInvoices(((data || []) as any[]).map((item) => ({
      id: item.id,
      invoiceNumber: item.invoice_number,
      clientName: item.client_name,
      issueDate: item.issue_date,
      dueDate: item.due_date,
      amount: Number(item.amount || 0),
      status: item.status,
      notes: item.notes || '',
      paidDate: item.paid_date || null,
      paidIncomeLogId: item.paid_income_log_id || null,
    })));
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void fetchInvoices();
  }, [fetchInvoices]);

  const saveInvoice = async () => {
    if (!user || !clientName.trim() || !number.trim() || !issueDate || !dueDate || !amount) return;
    setSaving(true);
    await supabase.from('work_invoices' as any).insert({
      user_id: user.id,
      invoice_number: number.trim(),
      client_name: clientName.trim(),
      issue_date: issueDate,
      due_date: dueDate,
      amount: Number(amount),
      notes: notes.trim() || null,
      status: 'draft',
    });
    setSaving(false);
    setShowForm(false);
    setNumber(`INV-${new Date().getFullYear()}-${String(Date.now()).slice(-4)}`);
    setClientName('');
    setIssueDate(new Date().toISOString().slice(0, 10));
    setDueDate(new Date().toISOString().slice(0, 10));
    setAmount('');
    setNotes('');
    notifySectionRefresh();
    onDataChanged?.();
    void fetchInvoices();
  };

  const updateStatus = async (invoiceId: string, status: WorkInvoiceStatus) => {
    await supabase.from('work_invoices' as any).update({ status }).eq('id', invoiceId);
    notifySectionRefresh();
    onDataChanged?.();
    void fetchInvoices();
  };

  const markInvoicePaid = async (invoice: WorkInvoice) => {
    if (!user) return;

    const paidDate = new Date().toISOString().slice(0, 10);
    let createdIncomeLogId: string | null = null;
    let linkedIncomeLogId = invoice.paidIncomeLogId || null;

    setPayingInvoiceId(invoice.id);

    if (!linkedIncomeLogId) {
      const { data: incomeEntry, error: incomeError } = await supabase
        .from('income_log' as any)
        .insert({
          user_id: user.id,
          amount: invoice.amount,
          source: `${invoice.clientName} · ${invoice.invoiceNumber}`,
          source_type: 'self_employed',
          project: 'other',
          notes: buildPaidIncomeNotes(invoice, paidDate),
          received_date: paidDate,
        })
        .select('id')
        .single();

      if (incomeError || !incomeEntry?.id) {
        console.error(incomeError);
        toast.error('Could not add that paid invoice into the income log.');
        setPayingInvoiceId(null);
        return;
      }

      linkedIncomeLogId = incomeEntry.id;
      createdIncomeLogId = incomeEntry.id;
    }

    const { error: invoiceError } = await supabase
      .from('work_invoices' as any)
      .update({
        status: 'paid',
        paid_date: invoice.paidDate || paidDate,
        paid_income_log_id: linkedIncomeLogId,
      })
      .eq('id', invoice.id)
      .eq('user_id', user.id);

    if (invoiceError) {
      console.error(invoiceError);

      if (createdIncomeLogId) {
        await supabase
          .from('income_log' as any)
          .delete()
          .eq('id', createdIncomeLogId)
          .eq('user_id', user.id);
      }

      toast.error('Could not finish marking that invoice as paid.');
      setPayingInvoiceId(null);
      return;
    }

    toast.success(
      createdIncomeLogId
        ? 'Invoice marked paid and added to income log.'
        : 'Invoice marked paid. Existing income log link kept.',
    );
    notifySectionRefresh();
    onDataChanged?.();
    setPayingInvoiceId(null);
    void fetchInvoices();
  };

  const stats = useMemo(() => {
    const openInvoices = invoices.filter((invoice) => invoice.status !== 'paid');
    const overdueCount = openInvoices.filter((invoice) =>
      differenceInDays(parseISO(invoice.dueDate), startOfDay(new Date())) < 0
    ).length;
    const outstanding = openInvoices.reduce((sum, invoice) => sum + invoice.amount, 0);

    return {
      open: openInvoices.length,
      overdue: overdueCount,
      outstanding,
    };
  }, [invoices]);
  const reminderQueue = useMemo(() => buildWorkInvoiceReminderQueue(invoices).slice(0, 3), [invoices]);

  return (
    <AppCard>
      <AppCardHeader>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <AppText fontSize="$sm" fontWeight="600" color="$colorMuted" textTransform="uppercase" letterSpacing={1.2}>
            Invoices
          </AppText>
          <AppText fontSize="$sm" color="$colorMuted" mt="$sm">
            Keep invoice numbers, email drafts, PDFs, due dates, and chasing status in the same place as the rest of your work admin.
          </AppText>
        </div>
        <AppButton size="sm" onPress={() => setShowForm((current) => !current)}>
          <Plus size={12} />
          {showForm ? 'Cancel' : 'Add'}
        </AppButton>
      </div>
      </AppCardHeader>

      <AppCardBody>
      <div className="grid grid-cols-3 gap-2 mb-4">
        <MetricCard label="Open" value={stats.open} />
        <MetricCard label="Overdue" value={stats.overdue} />
        <MetricCard label="£ live" value={Math.round(stats.outstanding)} />
      </div>

      {reminderQueue.length > 0 ? (
        <AppCard backgroundColor="$mutedBackground" p="$sm" className="mb-4">
          <div className="space-y-3">
            <div>
              <AppText fontSize="$sm" fontWeight="700">Chasing queue</AppText>
              <AppText fontSize="$sm" color="$colorMuted" mt="$xs">
                Adulting is now surfacing the next invoice action automatically so chasing does not live in your head.
              </AppText>
            </div>
            {reminderQueue.map((reminder) => {
              const invoice = invoices.find((entry) => entry.id === reminder.invoiceId);
              if (!invoice) return null;

              return (
                <AppCard key={reminder.invoiceId} backgroundColor="$background" p="$sm">
                  <AppText fontSize="$sm" fontWeight="700">
                    {reminder.invoiceNumber} · {reminder.clientName}
                  </AppText>
                  <AppText fontSize="$sm" color="$colorMuted" mt="$xs">
                    {reminder.body}
                  </AppText>
                  <AppButtonRow mt="$sm">
                    {reminder.action === 'send' ? (
                      <>
                        <AppButton size="sm" tone="outline" onPress={() => void copySendDraft(invoice)}>
                          <Copy size={12} />
                          Email draft
                        </AppButton>
                        <AppButton size="sm" tone="outline" onPress={() => void handleInvoiceDownload(invoice)}>
                          <Download size={12} />
                          Invoice PDF
                        </AppButton>
                        <AppButton size="sm" tone="outline" onPress={() => updateStatus(invoice.id, 'sent')}>
                          Mark sent
                        </AppButton>
                      </>
                    ) : (
                      <>
                        <AppButton size="sm" tone="outline" onPress={() => void copyChaseDraft(invoice)}>
                          <Copy size={12} />
                          Copy chase
                        </AppButton>
                        {invoice.status === 'sent' ? (
                          <AppButton size="sm" tone="outline" onPress={() => updateStatus(invoice.id, 'chasing')}>
                            Mark chasing
                          </AppButton>
                        ) : null}
                        <AppButton size="sm" tone="outline" onPress={() => handleInvoiceDownload(invoice)}>
                          <Download size={12} />
                          Invoice PDF
                        </AppButton>
                      </>
                    )}
                  </AppButtonRow>
                </AppCard>
              );
            })}
          </div>
        </AppCard>
      ) : null}

      {showForm && (
        <AppCard backgroundColor="$mutedBackground" p="$sm" className="animate-fade-in">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <AppFieldGroup label="Invoice number">
              <AppInput tag="input" value={number} onChange={(event) => setNumber((event.target as HTMLInputElement).value)} placeholder="Invoice number" />
            </AppFieldGroup>
            <AppFieldGroup label="Client name">
              <AppInput tag="input" value={clientName} onChange={(event) => setClientName((event.target as HTMLInputElement).value)} placeholder="Client name" />
            </AppFieldGroup>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <AppDateField label="Issue date" value={issueDate} onChange={setIssueDate} />
            <AppDateField label="Due date" value={dueDate} onChange={setDueDate} />
            <AppFieldGroup label="Amount">
              <AppInput tag="input" type="number" value={amount} onChange={(event) => setAmount((event.target as HTMLInputElement).value)} placeholder="Amount" />
            </AppFieldGroup>
          </div>
          <AppFieldGroup label="Notes">
            <AppTextArea value={notes} onChange={(event) => setNotes((event.target as HTMLTextAreaElement).value)} rows={3} placeholder="Notes, payment terms, chasing history…" />
          </AppFieldGroup>
          <AppButton
            width="100%"
            onPress={saveInvoice}
            disabled={!clientName.trim() || !number.trim() || !amount || saving}
          >
            {saving ? 'Saving…' : 'Save invoice'}
          </AppButton>
        </AppCard>
      )}

      {loading ? (
        <AppLoadingState title="Loading invoices" body="Pulling your saved invoices and chasing status." />
      ) : invoices.length === 0 ? (
        <AppEmptyState title="No invoices yet" body="Add the next one so due dates and chasing are visible." />
      ) : (
        <div className="space-y-3">
          {invoices.map((invoice) => {
            const prompt = getWorkInvoiceReminder(invoice);
            const promptClassName =
              prompt?.tone === 'error'
                ? 'border-red-200 bg-red-50 text-red-900'
                : prompt?.tone === 'warning'
                  ? 'border-amber-200 bg-amber-50 text-amber-900'
                  : 'border-sky-200 bg-sky-50 text-sky-900';

            return (
              <AppCard key={invoice.id} backgroundColor="$background" p="$sm">
                {prompt ? (
                  <div className={`mb-3 rounded-xl border px-3 py-3 ${promptClassName}`}>
                    <AppText fontSize="$sm" fontWeight="700">
                      {prompt.title}
                    </AppText>
                    <AppText fontSize="$sm" mt="$xs">
                      {prompt.body}
                    </AppText>
                  </div>
                ) : null}
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <AppText fontSize="$sm" fontWeight="700">{invoice.invoiceNumber}</AppText>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${statusClass(invoice)}`}>
                        {displayStatus(invoice)}
                      </span>
                    </div>
                    <AppText fontSize="$sm" mt="$xs">{invoice.clientName}</AppText>
                    <AppText fontSize="$sm" color="$colorMuted" mt="$xs">
                      Issued {format(parseISO(invoice.issueDate), 'd MMM yyyy')} · Due {format(parseISO(invoice.dueDate), 'd MMM yyyy')}
                    </AppText>
                    {invoice.status === 'paid' && invoice.paidDate ? (
                      <AppText fontSize="$sm" color="$colorMuted" mt="$xs">
                        Paid {format(parseISO(invoice.paidDate), 'd MMM yyyy')}
                        {invoice.paidIncomeLogId ? ' · logged to income' : ''}
                      </AppText>
                    ) : null}
                    {invoice.notes && (
                      <AppText fontSize="$sm" color="$colorMuted" mt="$sm">{invoice.notes}</AppText>
                    )}
                  </div>
                  <AppText fontSize="$sm" fontWeight="700">£{invoice.amount.toFixed(2)}</AppText>
                </div>
                <AppButtonRow mt="$sm">
                  <AppButton size="sm" tone="outline" onPress={() => handleInvoiceDownload(invoice)}>
                    <Download size={12} />
                    Invoice PDF
                  </AppButton>
                  {invoice.status === 'draft' && (
                    <AppButton size="sm" tone="outline" onPress={() => void copySendDraft(invoice)}>
                      <Copy size={12} />
                      Email draft
                    </AppButton>
                  )}
                  {invoice.status !== 'paid' && invoice.status !== 'draft' && (
                    <AppButton size="sm" tone="outline" onPress={() => void copyChaseDraft(invoice)}>
                      <Copy size={12} />
                      Chase draft
                    </AppButton>
                  )}
                  {invoice.status === 'draft' && (
                    <AppButton size="sm" tone="outline" onPress={() => updateStatus(invoice.id, 'sent')}>
                      Mark sent
                    </AppButton>
                  )}
                  {invoice.status === 'sent' && (
                    <AppButton size="sm" tone="outline" onPress={() => updateStatus(invoice.id, 'chasing')}>
                      Chasing
                    </AppButton>
                  )}
                  {invoice.status !== 'paid' && (
                    <AppButton
                      size="sm"
                      tone="outline"
                      onPress={() => void markInvoicePaid(invoice)}
                      disabled={payingInvoiceId === invoice.id}
                    >
                      {payingInvoiceId === invoice.id ? 'Logging income…' : 'Mark paid + log income'}
                    </AppButton>
                  )}
                </AppButtonRow>
              </AppCard>
          )})}
        </div>
      )}
      </AppCardBody>
    </AppCard>
  );
}

function MetricCard({ label, value }: { label: string; value: number }) {
  return (
    <AppCard backgroundColor="$background" p="$sm">
      <AppText fontSize="$sm" fontWeight="600" color="$colorMuted" textTransform="uppercase" letterSpacing={1.2}>
        {label}
      </AppText>
      <AppText fontSize="$xl" fontWeight="700" mt="$xs">{value}</AppText>
    </AppCard>
  );
}
